/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { change, reduxForm, SubmissionError } from 'redux-form'

import { breakpoint, matchesMediaQuery, pageLoad, parseURLParams, SmallText } from '@offerzen/pinata'

import featuredMobileImage from '../../../assets/signup-featured-mobile.svg'
import featuredImage from '../../../assets/signup-featured.svg'

import SignUpForm from '../../components/SignUpForm'
import Switch from '../../components/Switch'
import Heading from '../../components/Heading'
import SocialSignup from '../SocialSignup'
import { clearPlacesData, findPlaces } from '../../redux/modules/places'
import { createCandidate as reduxCreateCandidate, setLoading as reduxSetLoading } from '../../redux/modules/candidate'
import { updateProgress as reduxUpdateProgress } from '../../redux/modules/utility'
import { initialFormValues } from '../../selectors/createUser'
import useDocumentTitle from '../../components/DocumentTitle'

import styles from './styles'

const CreateUser = ({
  updateProgress,
  setLoading,
  location,
  refetchCurrentUser,
  candidate,
  places,
  addCity,
  searchCities,
  clearData,
  clearPlace,
  loading,
  handleSubmit,
  createCandidate,
}) => {
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true'

  useDocumentTitle('Developer Sign Up - OfferZen')

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const mountRecaptcha = () => {
    const script = document.createElement('script')
    script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`

    document.body.appendChild(script)
  }

  const setMarketingAffiliateCookie = () => {
    const marketingAffiliateCookieKey = 'PostbackValue='
    const urlParams = parseURLParams(location.search)

    const { clickid } = urlParams
    // clickid is tracking param used for performance marketing affiliate talent.com

    if (clickid) {
      const marketingAffiliateCookie = `${marketingAffiliateCookieKey}${clickid}`

      // Set cookie expiration date
      const expirationDate = new Date()
      expirationDate.setDate(expirationDate.getDate() + 15)

      document.cookie = `${marketingAffiliateCookie}; expires=${expirationDate.toUTCString()}`
    }
  }

  useEffect(() => {
    setMarketingAffiliateCookie()
  })

  useEffect(() => {
    if (recaptchaEnabled) {
      mountRecaptcha()
    }

    updateProgress(0)
    window.addEventListener('resize', () => {
      setWindowWidth(window.innerWidth)
    })
    pageLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth])

  // Function to wait for window.grecaptcha to become available
  const waitForRecaptcha = () => {
    return new Promise(resolve => {
      const intervalId = setInterval(() => {
        if (window.grecaptcha) {
          clearInterval(intervalId)
          resolve()
        }
      }, 100) // Check every 100 milliseconds
    })
  }

  const submitCreateCandidate = async data => {
    let recaptchaToken = null

    if (!data.based_in_location) {
      throw new SubmissionError({
        location: 'Please select a location from the suggestions',
      })
    }

    setLoading()
    const urlParams = parseURLParams(location.search)
    // rx_viewer, rx_job, irclickid are params used for performance marketing
    const { report_source, rx_viewer, rx_job, irclickid } = urlParams
    const referrer = window.location.href
    const url = window.location.href

    if (recaptchaEnabled) {
      await waitForRecaptcha().then(async () => {
        try {
          recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
            action: 'zadev',
          })
        } catch (error) {
          throw new Error(error)
        }
      })
    }

    // rx_viewer, rx_job, irclickid are params used for performance marketing
    createCandidate(
      {
        ...data,
        recaptchaToken,
        referrer,
        report_source,
        rx_viewer,
        rx_job,
        url,
        irclickid,
      },
      refetchCurrentUser,
    )
  }

  return (
    <div className={styles.container}>
      <Choose>
        <When condition={matchesMediaQuery(breakpoint.tablet)}>
          <div className={styles.columnContainer}>
            <div>
              <div className={styles.leftColumn}>
                <div className={styles.leftColumnContent}>
                  <h1>Make the best move of your developer career</h1>
                  <ul className={styles.infoList}>
                    <li>Get seen by over 2000 legit tech companies</li>
                    <li>Receive upfront role and salary info when companies reach out</li>
                    <li>Smash job interviews with your dedicated dev career coach</li>
                  </ul>
                  <img alt="featured" className={styles.featuredImage} src={featuredImage} />
                </div>
              </div>
            </div>
            <div>
              <div className={styles.card}>
                <div className={styles.headerSection}>
                  <Heading className={styles.headerheading}>Create your free profile</Heading>
                  <p className={styles.login}>
                    Already have an account? <a href="/users/sign_in">Log in </a>
                  </p>
                </div>
                <SocialSignup candidate={candidate} location={location} refetchCurrentUser={refetchCurrentUser} />
                <SignUpForm
                  onSubmit={handleSubmit(submitCreateCandidate)}
                  places={places}
                  candidate={candidate}
                  addCity={addCity}
                  searchCities={term => searchCities(term)}
                  clearData={clearData}
                  clearPlace={clearPlace}
                  loading={loading}
                />
              </div>
              <SmallText className={styles.recaptchaNotice}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" rel="noopener">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" rel="noopener">
                  Terms of Service
                </a>{' '}
                apply.
              </SmallText>
            </div>
          </div>
        </When>
        <Otherwise>
          <div className={styles.mobileContainer}>
            <Switch />
            <div className={styles.mobileHeader}>
              <h1>Make the best move of your developer career</h1>
              <img alt="mobile-featured" className={styles.featuredImage} src={featuredMobileImage} />
            </div>
            <div className={styles.card}>
              <SocialSignup candidate={candidate} location={location} refetchCurrentUser={refetchCurrentUser} />
              <SignUpForm
                onSubmit={handleSubmit(submitCreateCandidate)}
                places={places}
                candidate={candidate}
                addCity={addCity}
                searchCities={term => searchCities(term)}
                clearData={clearData}
                clearPlace={clearPlace}
                loading={loading}
              />
            </div>
            <div className={styles.mobileFooter}>
              <h2 className={styles.listLabel}>Why you&apos;ll love OfferZen:</h2>
              <ul className={styles.infoList}>
                <li>Get seen by over 2000 legit tech companies</li>
                <li>Receive upfront role and salary info when companies reach out</li>
                <li>Smash job interviews with your dedicated dev career coach</li>
              </ul>
            </div>
            <div className={styles.recaptchaNoticeContainer}>
              <SmallText className={styles.recaptchaNotice}>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" rel="noopener">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" rel="noopener">
                  Terms of Service
                </a>{' '}
                apply.
              </SmallText>
            </div>
          </div>
        </Otherwise>
      </Choose>
    </div>
  )
}

CreateUser.propTypes = {
  refetchCurrentUser: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  candidate: PropTypes.shape({}),
  places: PropTypes.shape({}),
  initialValues: PropTypes.shape({}),
  updateProgress: PropTypes.func,
  setLoading: PropTypes.func,
  location: PropTypes.shape({}),
  addCity: PropTypes.func,
  searchCities: PropTypes.func,
  clearData: PropTypes.func,
  clearPlace: PropTypes.func,
  handleSubmit: PropTypes.func,
  createCandidate: PropTypes.func,
}

const mapStateToProps = state => ({
  loading: state.candidate ? !!state.candidate.loading : false,
  candidate: state.candidate,
  places: state.places,
  initialValues: initialFormValues(state),
})

const mapDispatchToProps = dispatch => ({
  // To be cleaned up after feature is live
  searchCities: term => dispatch(findPlaces(term)),
  clearPlace: () => {
    dispatch(change('createUser', 'based_in_location', {}))
  },
  addCity: value => {
    dispatch(change('createUser', 'location', value.name))
    dispatch(change('createUser', 'based_in_location', value.based_in_location))
  },
  clearData: () => dispatch(clearPlacesData()),
  createCandidate: (data, refetchCurrentUser) => dispatch(reduxCreateCandidate(data, refetchCurrentUser)),
  setLoading: () => dispatch(reduxSetLoading()),
  updateProgress: progress => dispatch(reduxUpdateProgress(progress)),
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    reduxForm({
      form: 'createUser',
    })(CreateUser),
  ),
)
